/* global Safe2Pay */
/* eslint-env browser */

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    orderTotal: Number,
    baseTotal: Number,
    isSandbox: { type: Boolean, default: false },
    // 3DS Config
    isEnabled: { type: Boolean, default: true },
    isDebug: { type: Boolean, default: false },
    isChallengeSuppressed: { type: Boolean, default: false },
    // Payment Config
    currency: { type: String, default: 'BRL' },
    installments: { type: String, default: '01' },
    orderRecurrence: { type: Boolean, default: false },
    orderProductcode: { type: String, default: 'PHY' },
    shipToShippingmethod: { type: String, default: 'lowcost' },
    // Error Messages
    errorFailure: { type: String, default: 'Não foi possível concluir a autenticação do cartão. Por favor, tente novamente ou utilize outro método de pagamento.' },
    errorUnenrolled: { type: String, default: 'O cartão utilizado não está habilitado para autenticação em compras online. Por favor, entre em contato com o emissor do cartão ou tente utilizar outro método de pagamento.' },
    errorDisabled: { type: String, default: 'Autenticação desabilitada pela loja.' },
    errorUnsupportedBrand: { type: String, default: 'A bandeira do cartão não suportada para autenticação. Por favor, entre em contato com o emissor do cartão ou tente utilizar outro método de pagamento.' },
    // Warning Messages
    warningAuthFailureTitle: { type: String, default: 'Falha na autenticação' }
  }

  static targets = [
    'form',
    'submitButton',
    'cardNumber',
    'expiration',
    'holder',
    'holderDocument',
    'cvv',
    'cavv',
    'xid',
    'eci',
    'referenceId',
    'version',
    'warningTemplate',
    'creditCardFields',
    'adjustmentLine',
    'adjustmentLabel',
    'adjustmentAmount',
    'totalValue'
  ]

  connect () {
    this.isAuthenticated = false
    this.isInitialized = false
    this.isInitializing = false
    this.needsReload = false
  }

  select (event) {
    this.element.dataset.selected = event.target.value.replace('_', '-')

    const radio = event.target
    const type = radio.dataset.adjustmentType || 'none'
    const kind = radio.dataset.adjustmentKind || 'percentage'
    const value = parseFloat(radio.dataset.adjustmentValue) || 0
    const title = radio.dataset.adjustmentTitle || ''

    this.#toggleCreditCardRequired(this.#isOnlineCredit())
    this.#applyAdjustment(type, kind, value, title)
  }

  submit (event) {
    if (this.#isOnlineCredit()) {
      if (this.isAuthenticated) return

      event.preventDefault()

      if (!this.#validateCreditCardFields()) return

      this.submitButtonTarget.disabled = true

      if (!this.isInitialized) {
        this.#initialize3DS()
      } else {
        this.#handleCheckout()
      }
    }
  }

  // Private

  #applyAdjustment (type, kind, value, title) {
    if (type === 'none' || value === 0) {
      if (this.hasAdjustmentLineTarget) {
        this.adjustmentLineTarget.classList.add('hidden')
      }
      this.#updateTotal(this.baseTotalValue)
      return
    }

    const baseTotal = this.baseTotalValue
    const amount = kind === 'percentage'
      ? Math.round(baseTotal * value / 100 * 100) / 100
      : value

    if (this.hasAdjustmentLineTarget) {
      this.adjustmentLineTarget.classList.remove('hidden')

      if (type === 'fee') {
        this.adjustmentLabelTarget.className = 'text-sm text-black/70'
        this.adjustmentLabelTarget.textContent = `Taxa de pagamento (${title})`
        this.adjustmentAmountTarget.className = 'text-sm font-medium'
        this.adjustmentAmountTarget.textContent = this.#formatCurrency(amount)
      } else {
        this.adjustmentLabelTarget.className = 'text-sm text-green-600'
        this.adjustmentLabelTarget.textContent = `Desconto (${title})`
        this.adjustmentAmountTarget.className = 'text-sm text-green-600'
        this.adjustmentAmountTarget.textContent = this.#formatCurrency(-amount)
      }
    }

    const newTotal = type === 'fee'
      ? baseTotal + amount
      : baseTotal - amount

    this.#updateTotal(newTotal)
  }

  #updateTotal (newTotal) {
    this.orderTotalValue = newTotal

    if (this.hasTotalValueTarget) {
      this.totalValueTarget.textContent = this.#formatCurrency(newTotal)
    }
  }

  #formatCurrency (value) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
  }

  #isOnlineCredit () {
    return this.element.dataset.selected === 'online-credit'
  }

  #toggleCreditCardRequired (required) {
    if (!this.hasCreditCardFieldsTarget) return

    const inputs = this.creditCardFieldsTarget.querySelectorAll('input:not([type=hidden])')
    inputs.forEach(input => { input.required = required })
  }

  #validateCreditCardFields () {
    const fields = [
      this.cardNumberTarget,
      this.expirationTarget,
      this.cvvTarget,
      this.holderTarget,
      this.holderDocumentTarget
    ]

    let valid = true
    for (const field of fields) {
      if (!field.value.trim()) {
        field.focus()
        field.reportValidity()
        valid = false
        break
      }
    }
    return valid
  }

  #initialize3DS () {
    this.isInitializing = true

    const setup = () => {
      const eventHandlers = {
        onReady: this.#handleOnReady.bind(this),
        onSuccess: this.#handleOnSuccess.bind(this),
        onFailure: (e) => this.#handleEvent(e, this.errorFailureValue),
        onUnenrolled: (e) => this.#handleEvent(e, this.errorUnenrolledValue),
        onDisabled: (e) => this.#handleEvent(e, this.errorDisabledValue),
        onUnsupportedBrand: (e) => this.#handleEvent(e, this.errorUnsupportedBrandValue),
        onError: (e) => this.#handleOnError(e)
      }

      for (const [event, handler] of Object.entries(eventHandlers)) {
        Safe2Pay.Mpi.addEventListener(event, handler)
      }

      Safe2Pay.Mpi.Init(this.#config, this.orderTotalValue)
    }

    if (this.needsReload || typeof Safe2Pay === 'undefined') {
      this.#reloadSdkScript(setup)
    } else {
      setup()
    }
  }

  #handleCheckout () {
    Safe2Pay.Mpi.Checkout(this.#payment)
  }

  #handleOnReady () {
    this.isInitializing = false
    this.isInitialized = true
    this.#handleCheckout()
  }

  #handleOnSuccess (event) {
    this.cavvTarget.value = event.Cavv
    this.xidTarget.value = event.Xid
    this.eciTarget.value = event.Eci
    this.versionTarget.value = event.Version
    this.referenceIdTarget.value = event.ReferenceId

    this.isAuthenticated = true
    this.formTarget.requestSubmit()
  }

  #handleOnError (event) {
    console.error('[PAYMENT_CHANGE]', event)

    if (this.isInitializing) return

    this.#resetAfterFailure()
  }

  #handleEvent (_event, message) {
    this.#resetAfterFailure()
    this.#showWarning(this.warningAuthFailureTitleValue, message)
  }

  #resetAfterFailure () {
    this.isInitialized = false
    this.isInitializing = false
    this.needsReload = true
    this.submitButtonTarget.disabled = false
  }

  #showWarning (title, description) {
    if (!this.hasWarningTemplateTarget) return

    document.getElementById('payment_change_warning_modal')?.remove()

    const html = this.warningTemplateTarget.innerHTML
      .replace('{{TITLE}}', title)
      .replace('{{DESCRIPTION}}', description)

    const container = document.getElementById('master_turbo') || document.body
    container.insertAdjacentHTML('beforeend', html)
  }

  #reloadSdkScript (callback) {
    const src = 'https://static.safe2pay.dev/scripts/verify_3DS2.min.js'
    const oldScript = document.querySelector(`script[src="${src}"]`)

    if (oldScript) oldScript.remove()
    try { delete window.Safe2Pay } catch (_) { window.Safe2Pay = undefined }

    const newScript = document.createElement('script')
    newScript.src = src
    newScript.onload = callback
    document.head.appendChild(newScript)
  }

  get #payment () {
    return {
      currency: this.currencyValue,
      installments: this.installmentsValue,
      order_recurrence: this.orderRecurrenceValue,
      order_productcode: this.orderProductcodeValue,
      shipTo_shippingmethod: this.shipToShippingmethodValue,
      billto_customerid: this.holderDocumentTarget.value,
      billto_contactname: this.holderTarget.value,
      cardnumber: this.cardNumberTarget.value.replace(/\D/g, ''),
      cardexpirationmonth: this.#cardExpirationMonth,
      cardexpirationyear: this.#cardExpirationYear,
      cardalias: this.holderTarget.value
    }
  }

  get #config () {
    return {
      IsEnabled: this.isEnabledValue,
      IsSandbox: this.isSandboxValue,
      IsDebug: this.isDebugValue,
      IsChallengeSuppressed: this.isChallengeSuppressedValue
    }
  }

  get #cardExpirationMonth () {
    return this.expirationTarget.value.split('/')[0]
  }

  get #cardExpirationYear () {
    return this.expirationTarget.value.split('/')[1]
  }
}
